"use client";

import React, { useEffect, useRef } from "react";

type GuestbookSignaturePadProps = {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
};

export default function GuestbookSignaturePad({
  value,
  onChange,
  disabled = false,
}: GuestbookSignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(Boolean(value));

  useEffect(() => {
    if (value) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawingRef.current = false;
    hasInkRef.current = false;
  }, [value]);

  function getPoint(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  function startDrawing(event: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    event.preventDefault();

    const canvas = canvasRef.current;
    const point = getPoint(event);
    if (!canvas || !point) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    drawingRef.current = true;
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {}

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#1f4e79";
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  }

  function draw(event: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    if (!drawingRef.current) return;
    event.preventDefault();

    const canvas = canvasRef.current;
    const point = getPoint(event);
    if (!canvas || !point) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    hasInkRef.current = true;
  }

  function finishDrawing(event?: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    event?.preventDefault();

    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (event) {
      try {
        canvas.releasePointerCapture(event.pointerId);
      } catch {}
    }

    onChange(hasInkRef.current ? canvas.toDataURL("image/png") : null);
  }

  function clearCanvas() {
    if (disabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    drawingRef.current = false;
    hasInkRef.current = false;
    onChange(null);
  }

  return (
    <div className={disabled ? "space-y-3 opacity-70" : "space-y-3"}>
      <div className="overflow-hidden rounded-[1.5rem] border border-[#decf9b] bg-white shadow-sm">
        <canvas
          ref={canvasRef}
          width={900}
          height={260}
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={finishDrawing}
          onPointerLeave={finishDrawing}
          onPointerCancel={finishDrawing}
          aria-disabled={disabled}
          className={disabled
            ? "h-40 w-full cursor-not-allowed touch-none bg-[linear-gradient(180deg,#fffdfa_0%,#f7f1e5_100%)]"
            : "h-40 w-full touch-none bg-[linear-gradient(180deg,#fffdfa_0%,#f7f1e5_100%)]"
          }
          style={{ touchAction: "none" }}
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-7 text-[#686861]">
          Sign with an Apple Pencil, your finger, or a phone touch screen.
        </p>

        <button
          type="button"
          onClick={clearCanvas}
          disabled={disabled}
          className="inline-flex min-h-12 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#f8f5ed] px-5 py-2 text-sm font-semibold text-[#1f4e79] transition hover:bg-[#f2ecdf]"
        >
          Clear Signature
        </button>
      </div>

      {value ? (
        <div className="rounded-[1.25rem] border border-[#decf9b] bg-[#fffdfa] px-4 py-3">
          <p className="text-xs uppercase tracking-[.18em] text-[#8c8c86]">
            Signature Preview Saved
          </p>
        </div>
      ) : null}
    </div>
  );
}
